import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { ChatMessage, ChatUser, ChatInitialData } from '@my-game/shared-types';
import { chatSocket } from '../lib/chatSocket';
import { useGame } from './GameContext';
import config from '../config';

interface ChatState {
  messages: ChatMessage[];
  users: ChatUser[];
  isConnected: boolean;
  error: string | null;
}

type ChatAction =
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_INITIAL_DATA'; payload: ChatInitialData }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'USER_JOINED'; payload: ChatUser }
  | { type: 'USER_LEFT'; payload: ChatUser }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_MESSAGES' };

const initialState: ChatState = {
  messages: [],
  users: [],
  isConnected: false,
  error: null
};

const MAX_MESSAGES = 100;

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_CONNECTED':
      return { ...state, isConnected: action.payload };
      
    case 'SET_INITIAL_DATA':
      return {
        ...state,
        messages: action.payload.messages.slice(-MAX_MESSAGES),
        users: action.payload.users,
        error: null
      };
      
    case 'ADD_MESSAGE':
      const newMessages = [...state.messages, action.payload];
      // Ограничиваем количество сообщений
      if (newMessages.length > MAX_MESSAGES) {
        return { ...state, messages: newMessages.slice(-MAX_MESSAGES) };
      }
      return { ...state, messages: newMessages };
      
    case 'USER_JOINED':
      // Проверяем, что пользователь еще не в списке
      if (state.users.some(u => u.characterId === action.payload.characterId)) {
        return state;
      }
      return { ...state, users: [...state.users, action.payload] };
      
    case 'USER_LEFT':
      return {
        ...state,
        users: state.users.filter(u => u.characterId !== action.payload.characterId)
      };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload };
      
    case 'CLEAR_MESSAGES':
      return { ...state, messages: [] };
      
    default:
      return state;
  }
}

interface ChatContextValue {
  state: ChatState;
  sendMessage: (text: string) => void;
  clearError: () => void;
  isAvailable: boolean;
}

const ChatContext = createContext<ChatContextValue | null>(null);

// Внутренний компонент, который рендерится только когда чат доступен
function ChatProviderInner({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Эффект для подключения к чату - запустится только один раз
  useEffect(() => {
    console.log('[ChatContext] Initializing chat connection...');
    
    // Подключаемся к чату
    chatSocket.connect(config.api.wsUrl);
    dispatch({ type: 'SET_CONNECTED', payload: true });

    // Подписываемся на события
    const unsubscribers: (() => void)[] = [];

    unsubscribers.push(
      chatSocket.on('chat.initial-data', (data) => {
        console.log('ChatContext: Received initial data', data);
        dispatch({ type: 'SET_INITIAL_DATA', payload: data });
      })
    );

    unsubscribers.push(
      chatSocket.on('chat.new-message', (message) => {
        console.log('ChatContext: New message received', message);
        dispatch({ type: 'ADD_MESSAGE', payload: message });
      })
    );

    unsubscribers.push(
      chatSocket.on('chat.user-joined', (user) => {
        dispatch({ type: 'USER_JOINED', payload: user });
      })
    );

    unsubscribers.push(
      chatSocket.on('chat.user-left', (user) => {
        dispatch({ type: 'USER_LEFT', payload: user });
      })
    );

    unsubscribers.push(
      chatSocket.on('chat.error', (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      })
    );

    // Cleanup - вызовется только при размонтировании
    return () => {
      console.log('[ChatContext] Cleaning up chat connection...');
      
      // Отписываемся от всех событий
      unsubscribers.forEach(unsub => unsub());
      
      // Отключаемся от чата
      chatSocket.disconnect();
      
      // Очищаем состояние
      dispatch({ type: 'SET_CONNECTED', payload: false });
      dispatch({ type: 'CLEAR_MESSAGES' });
    };
  }, []); // Пустой массив зависимостей - эффект запустится только один раз

  const sendMessage = useCallback((text: string) => {
    if (!state.isConnected) {
      dispatch({ type: 'SET_ERROR', payload: 'Чат не подключен' });
      return;
    }

    if (!text.trim()) {
      return;
    }

    chatSocket.emit('chat.send-message', { text: text.trim() });
  }, [state.isConnected]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const value: ChatContextValue = {
    state,
    sendMessage,
    clearError,
    isAvailable: true
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

// Основной компонент провайдера с условным рендерингом
export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { state: gameState } = useGame();

  // Проверяем все условия для доступности чата
  const isChatAvailable = 
    gameState.character && 
    gameState.socketConnected && 
    gameState.character.current_location_id?.type !== 'traveling';

  // Если чат недоступен, рендерим заглушку
  if (!isChatAvailable) {
    const stubValue: ChatContextValue = {
      state: {
        ...initialState,
        error: gameState.character?.current_location_id?.type === 'traveling' 
          ? 'Чат недоступен во время путешествия' 
          : 'Чат недоступен'
      },
      sendMessage: () => {},
      clearError: () => {},
      isAvailable: false
    };

    return (
      <ChatContext.Provider value={stubValue}>
        {children}
      </ChatContext.Provider>
    );
  }

  // Если все условия выполнены, рендерим настоящий провайдер
  return <ChatProviderInner>{children}</ChatProviderInner>;
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
}
